import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, FileText } from "lucide-react";
import { toast } from "sonner";
import { StatementDialog } from "@/components/accounting/StatementDialog";
import { hasOutstandingBalance, fm } from "@/components/payables/shared";

export function VendorsTab() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "", tax_id: "", notes: "" });
  const [stmtParty, setStmtParty] = useState(null);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/vendors");
      setRows(data);
    } catch (err) {
      console.warn("[Payables] load vendors failed:", err?.message);
      toast.error("Couldn't load vendors");
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    try {
      if (editing) await api.patch(`/vendors/${editing.id}`, form);
      else await api.post("/vendors", form);
      toast.success("Saved"); setOpen(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this vendor?")) return;
    try { await api.delete(`/vendors/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); }
  };
  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="vendor-new-btn" onClick={() => { setEditing(null); setForm({ name: "", email: "", phone: "", address: "", tax_id: "", notes: "" }); setOpen(true); }}
              className="rounded-md btn-brand h-10 px-4">
              <Plus className="w-4 h-4 mr-2" /> New vendor
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>{editing ? "Edit vendor" : "New vendor"}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Name</Label><Input data-testid="vendor-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              <div className="col-span-2"><Label>Address</Label><Textarea rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
              <div><Label>Tax ID</Label><Input value={form.tax_id} onChange={(e) => setForm({ ...form, tax_id: e.target.value })} /></div>
              <div className="col-span-2"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button data-testid="vendor-form-save" onClick={save} className="rounded-full bg-[#2F4F4F] hover:bg-[#213838] text-[#FDFCF8]">Save</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      <div className="card-flat overflow-hidden">
        {rows.length === 0 ? (
          <div className="p-10 text-center text-sm text-[var(--muted-text)]">No vendors yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[var(--hover)] text-xs uppercase tracking-wider text-[var(--muted-text)]">
              <tr><th className="text-left px-5 py-2.5">Name</th><th className="text-left px-4 py-2.5">Email</th><th className="text-right px-4 py-2.5">Balance</th><th className="w-24" /></tr>
            </thead>
            <tbody>
              {rows.map((v) => (
                <tr key={v.id} data-testid={`vendor-row-${v.id}`} className="border-b border-[var(--divider)] last:border-none">
                  <td className="px-5 py-3 font-medium">{v.name}</td>
                  <td className="px-4 py-3 text-[var(--muted-text)]">{v.email || "—"}</td>
                  <td className="px-4 py-3 text-right font-mono">
                    {Array.isArray(v.balance_by_currency) && v.balance_by_currency.length > 1 ? (
                      <div className="flex flex-col items-end gap-0.5" data-testid={`vendor-balance-mixed-${v.id}`}>
                        {v.balance_by_currency.map((b) => (
                          <span key={b.currency} className="inline-flex items-center gap-1.5">
                            <span className="text-[10px] px-1 rounded font-mono text-[#FDFCF8]"
                                  style={{ backgroundColor: b.currency === "USD" ? "#1E5A96" : "#4C956C" }}>
                              {b.currency}
                            </span>
                            {fm(b.outstanding, b.currency)}
                          </span>
                        ))}
                      </div>
                    ) : (
                      fm(
                        v.balance ?? (v.balance_by_currency?.[0]?.outstanding || 0),
                        v.balance_by_currency?.[0]?.currency || "USD"
                      )
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {hasOutstandingBalance(v) && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setStmtParty(v)}
                        data-testid={`vendor-statement-btn-${v.id}`}
                        className="mr-1 h-8 px-2 text-xs"
                        title="Vendor Statement"
                      >
                        <FileText className="w-3.5 h-3.5 mr-1" /> Statement
                      </Button>
                    )}
                    <Button size="icon" variant="ghost" onClick={() => { setEditing(v); setForm({ ...v }); setOpen(true); }}><Pencil className="w-4 h-4" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => remove(v.id)}><Trash2 className="w-4 h-4 text-[#E63946]" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <StatementDialog
        open={!!stmtParty}
        onOpenChange={(v) => !v && setStmtParty(null)}
        kind="vendor"
        party={stmtParty}
      />
    </div>
  );
}
